import { AnswerOptionType } from '@/client/quizzes/enums';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { RichTextRenderer } from '@/ui/rich-text-renderer';
import { FlexContainer } from '@/ui/styled-ui';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton, RadioButtonChangeEvent } from 'primereact/radiobutton';
import React from 'react';
import styled from 'styled-components';

const StyledQuizAnswerOptionContainer = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: var(--small-padding);

  .answer-option-text {
    margin-left: var(--small-padding);
  }

  label {
    display: flex;
    align-items: center;
    gap: var(--xsmall-padding);
    cursor: pointer;
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);

    .p-checkbox {
      margin-right: 7px;
    }
  }
`;

const StyledCustomInputTextarea = styled(InputTextarea)`
  width: 100%;
  height: 127px;
  border-radius: var(--small-border-radius);
  border: 1px solid var(--gray-moderate);
  padding: var(--medium-padding);
  font-size: var(--small-font-size);
  margin-top: var(--xsmall-padding);
  resize: none !important;
  background-color: var(--white-main);

  &:focus {
    border-color: var(--primary-main);
    box-shadow: 0 0 0 1px var(--primary-main);
  }
`;

type QuizAnswerOptionProps = {
  answerOption: {
    id: string;
    text: string;
    isCorrect: boolean;
    type: AnswerOptionType;
  };
  isSelected: boolean;
  onOptionSelect: (e: CheckboxChangeEvent | RadioButtonChangeEvent) => void;
  isMultiChoice?: boolean;
  textResponse?: string;
  onTextResponseChange?: (value: string) => void;
  textResponseMaxLength?: number;
  placeholderText?: string;
  tooltipText?: string;
  groupName: string;
};

export const QuizAnswerOption: React.FC<QuizAnswerOptionProps> = ({
  answerOption,
  isSelected,
  onOptionSelect,
  isMultiChoice,
  textResponse,
  onTextResponseChange,
  textResponseMaxLength,
  placeholderText,
  tooltipText,
  groupName,
}) => {
  return (
    <StyledQuizAnswerOptionContainer>
      {isMultiChoice ? (
        <label>
          <Checkbox
            checked={isSelected}
            type="checkbox"
            onChange={onOptionSelect}
            inputId={answerOption.id}
          />
          <div>
            <RichTextRenderer content={answerOption.text} />
          </div>
          {tooltipText && (
            <AppIconTooltip
              target={`tooltip-${answerOption.id}`}
              content={tooltipText}
              position="top"
            />
          )}
        </label>
      ) : (
        <label>
          <RadioButton
            name={groupName}
            checked={isSelected}
            onChange={onOptionSelect}
            className="p-circle"
            type="radio"
            inputId={answerOption.id}
          />
          <div>
            <RichTextRenderer content={answerOption.text} />
          </div>
          {tooltipText && (
            <AppIconTooltip
              target={`tooltip-${answerOption.id}`}
              content={tooltipText}
              position="top"
            />
          )}
        </label>
      )}
      {answerOption.type === AnswerOptionType.CUSTOM_INPUT &&
        isSelected &&
        onTextResponseChange && (
          <StyledCustomInputTextarea
            value={textResponse || ''}
            onChange={(e) => onTextResponseChange(e.target.value)}
            maxLength={textResponseMaxLength}
            placeholder={placeholderText}
            required
          />
        )}
    </StyledQuizAnswerOptionContainer>
  );
};
